import Flatpickr from 'flatpickr';
import { ConfirmDatePlugin } from './flatpickr-plugins/confirm-date';

import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';

import { LocaleService } from '../../locale/locale.service';
import { Subscription } from '../../util/rxjs';

@Component({
  selector: 'cp-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.less']
})
export class DatePickerComponent implements AfterViewInit, OnInit, OnDestroy {
  @Input() cpFormControl: UntypedFormControl;

  @Input() showErrors = true;

  @Input() validate = true;

  @Input() static = false;

  @Input() startFromToday = false;

  @ViewChild('flatpickrElement') flatpickrElement: ElementRef;

  @ViewChild('dateInput') dateInput: ElementRef;

  @Input()
  get cpModel() {
    return this.cpModelValue;
  }

  @Output() cpModelChange = new EventEmitter<moment.Moment | undefined>();

  placeholder: string;

  set cpModel(value: moment.Moment | undefined) {
    this.cpModelValue = value;
    this.setFlatpickrValue(value);
    this.cpModelChange.emit(value);
  }

  get formattedValue() {
    return this.cpModel && this.localeService.formatMoment(this.cpModel, 'L LT');
  }

  private localeSubscription: Subscription;
  private cpModelValue: moment.Moment | undefined;
  private flatpickr: Flatpickr.Instance;

  constructor(private localeService: LocaleService, private cd: ChangeDetectorRef) {}

  ngOnInit() {
    this.cpFormControl = this.cpFormControl || new UntypedFormControl();
  }

  ngAfterViewInit() {
    this.localeSubscription = this.localeService.locale$.subscribe(locale => {
      this.createFlatpickr(locale);
      this.refreshModel();
      this.setLabel();
    });
  }

  ngOnDestroy() {
    if (this.localeSubscription) {
      this.localeSubscription.unsubscribe();
    }
    if (this.flatpickr) {
      this.flatpickr.destroy();
    }
  }

  open() {
    this.flatpickr.open();
  }

  parseDate() {
    if (!this.cpFormControl.value) {
      this.cpModel = undefined;
    } else {
      this.cpModel = moment(this.cpFormControl.value, 'L LT');
      if (!this.cpModel.isValid()) {
        this.cpModel = moment(new Date(this.cpFormControl.value));
      }
    }
    this.cd.detectChanges();
  }

  private createFlatpickr(localeName: string) {
    if (this.flatpickr) {
      this.flatpickr.destroy();
    }

    const locale = this.localeService.flatpickrLocale;
    const options = {
      locale,
      time_24hr: locale.is24Hour,
      enableTime: true,
      allowInput: true,
      static: this.static,
      onChange: (date: Date[]) => this.setInputValue(date),
      plugins: [ConfirmDatePlugin({ confirmText: '' })]
    } as any;
    const startDate = this.startFromToday
      ? {
          minDate: new Date(
            moment()
              .toDate()
              .toDateString()
          )
        }
      : {};

    this.flatpickr = Flatpickr(this.flatpickrElement.nativeElement, {
      ...startDate,
      ...options
    }) as Flatpickr.Instance;
    this.setFlatpickrValue(this.cpModelValue);
  }

  private refreshModel() {
    this.cpModel = this.cpModel && moment(this.cpModel);
    this.cd.detectChanges();
  }

  private setInputValue(date: Date[]) {
    if (date.length !== 0) {
      this.cpModel = moment(date[0]);
      this.cd.detectChanges();
    }
  }

  private setFlatpickrValue(value: moment.Moment | undefined) {
    if (!this.flatpickr) {
      return;
    }

    if (!value) {
      this.flatpickr.clear();
    } else {
      this.flatpickr.setDate(value.toDate());
    }
    this.cd.detectChanges();
  }

  private setLabel() {
    this.placeholder = `${moment
      .localeData()
      .longDateFormat('L')} ${moment.localeData().longDateFormat('LT')}`;
    this.cd.detectChanges();
  }
}
